type NotificationWithType = { id: string; notification_type: string }
type ActiveDeadlineNotice = {
  revision_id: string
  notification_id: string | null
  gameweek_number: number
}

export type InboxItem<
  TNotification extends NotificationWithType,
  TNotice extends ActiveDeadlineNotice
> =
  | { kind: 'deadline'; id: string; notice: TNotice }
  | { kind: 'notification'; id: string; notification: TNotification }

export function hideActiveDeadlineReceipts<T extends NotificationWithType>(
  notifications: readonly T[],
  notices: readonly { notification_id: string | null }[]
): T[] {
  const activeReceiptIds = new Set(
    notices.map((notice) => notice.notification_id).filter((id): id is string => id !== null)
  )
  return notifications.filter(
    (notification) =>
      notification.notification_type !== 'gameweek_deadline' ||
      !activeReceiptIds.has(notification.id)
  )
}

export function buildInboxItems<
  TNotification extends NotificationWithType & { is_read: boolean },
  TNotice extends ActiveDeadlineNotice
>(
  notifications: readonly TNotification[],
  notices: readonly TNotice[],
  unreadOnly: boolean,
  typeFilter: string | null
): InboxItem<TNotification, TNotice>[] {
  const active: InboxItem<TNotification, TNotice>[] =
    typeFilter && typeFilter !== 'gameweek_deadline'
      ? []
      : [...notices]
          .sort((left, right) => left.gameweek_number - right.gameweek_number)
          .map((notice) => ({
            kind: 'deadline' as const,
            id: `deadline:${notice.revision_id}`,
            notice
          }))
  const ordinary: InboxItem<TNotification, TNotice>[] = hideActiveDeadlineReceipts(
    notifications,
    notices
  )
    .filter((notification) => !unreadOnly || !notification.is_read)
    .filter((notification) => !typeFilter || notification.notification_type === typeFilter)
    .map((notification) => ({
      kind: 'notification' as const,
      id: notification.id,
      notification
    }))
  return [...active, ...ordinary]
}

type ScheduleNoticeDetails = {
  revision_id: string
  gameweek_number: number
  decision: 'updated' | 'late_schedule_conflict' | 'safety_hold' | 'missing_fixture'
  title: string
  message: string
  old_deadline: string | null
  effective_deadline: string | null
}

export type ScheduleNotificationView = {
  id: string
  notification_type: 'gameweek_deadline'
  priority: 'high'
  is_read: false
  created_at: null
  title: string
  message: string
  details: string | null
  action_url: string | null
  action_label: string | null
}

const formatDeadline = (value: string): string | null => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
    date
  )
}

export function toScheduleNotificationView(
  notice: ScheduleNoticeDetails,
  actionUrl: string | null
): ScheduleNotificationView {
  const current = notice.effective_deadline && formatDeadline(notice.effective_deadline)
  const previous = notice.old_deadline && formatDeadline(notice.old_deadline)
  const currentLabel =
    notice.decision === 'safety_hold'
      ? 'Published deadline (schedule under review)'
      : 'Current deadline'
  const details =
    notice.decision !== 'missing_fixture' && current
      ? `${currentLabel}: ${current}${notice.decision === 'updated' && previous && previous !== current ? ` · Previously ${previous}` : ''}`
      : null
  return {
    id: `deadline:${notice.revision_id}`,
    notification_type: 'gameweek_deadline',
    priority: 'high',
    is_read: false,
    created_at: null,
    title: `Gameweek ${notice.gameweek_number} · ${notice.title}`,
    message: notice.message,
    details,
    action_url: actionUrl,
    action_label: actionUrl ? 'Open squad' : null
  }
}
